import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft, 
  RefreshCw, 
  GripVertical, 
  Clock, 
  Users, 
  AlertTriangle,
  Sparkles,
  FileSpreadsheet,
  Calendar,
  Layers,
  Edit2,
  ArrowRightLeft,
  Loader2,
  Trash2,
  PlusCircle,
  Link2,
  Unlink,
  Lock,
  LockOpen,
  Copy,
  Sun,
  Sunset,
  Moon,
  Pin,
  PinOff,
  Scissors,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatGradeLabel } from '@/utils/gradeLabels';

// Format seconds to mm:ss or h:mm:ss
const formatTime = (seconds) => {
  if (!seconds) return '0:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

// Format seconds to human readable
const formatDuration = (seconds) => {
  if (!seconds) return '0 min';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) {
    return `${hrs}h ${mins}m`;
  }
  return `${mins} min`;
};

// Format 24-hour time string to 12-hour format (e.g., "18:13" -> "06:13 pm")
const formatTimeTo12Hour = (timeStr) => {
  if (!timeStr || timeStr === '--:--') return '--:-- --';
  try {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const hours12 = hours % 12 || 12;
    return `${String(hours12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
  } catch {
    return timeStr;
  }
};

// Inline editable duration chip for break rows. Displays in the same
// human-readable form as event rows (e.g. "30 min" / "1h 0m") via
// ``formatDuration``. Click to edit — the editor accepts ``h:mm``
// (matching the Competition Breaks config: 0:15 = 15 min, 1:00 = 1h,
// 1:30 = 90 min). Enter / blur commits, Escape cancels.
//
// When ``disabled`` is true (OOS locked) the chip stops opening the
// editor on click and renders dimmed — the value is still readable.
function BreakDurationEditor({ event, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const secs = Number(event.event_time_estimated_seconds || 0);
  // Human-readable label mirroring event rows.
  const display = formatDuration(secs);
  // h:mm seed for the editor input (rounded to the nearest minute —
  // breaks are always configured in minutes).
  const totalMinutes = Math.round(secs / 60);
  const editSeed = `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`;

  const beginEdit = (e) => {
    e.stopPropagation();
    if (disabled) return;
    setDraft(editSeed);
    setEditing(true);
  };

  const commit = async () => {
    const raw = (draft || '').trim();
    const match = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
      toast.error('Enter break length as h:mm (e.g. 0:15 for 15 min, 1:00 for 1 hour).');
      setEditing(false);
      return;
    }
    const h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (m >= 60) {
      toast.error('Minutes must be 0–59 (e.g. 1:30 for 1h 30min).');
      setEditing(false);
      return;
    }
    const seconds = h * 3600 + m * 60;
    if (seconds === secs) {
      setEditing(false);
      return;
    }
    setEditing(false);
    if (onSave) await onSave(event.programme_id, seconds);
  };

  if (editing) {
    return (
      <input
        type="text"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-16 px-1 py-0 text-xs text-center border border-amber-400 rounded bg-white"
        placeholder="h:mm"
        data-testid={`break-duration-input-${event.programme_id}`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={beginEdit}
      disabled={disabled}
      title={disabled ? 'Order of Skate is locked — unlock to edit break length' : 'Break length — click to edit (h:mm)'}
      className={`inline-flex items-center justify-center gap-1 text-sm rounded px-1.5 py-0.5 transition-colors ${
        disabled
          ? 'text-stone-400 cursor-not-allowed'
          : 'text-amber-700 hover:bg-amber-100'
      }`}
      data-testid={`break-duration-chip-${event.programme_id}`}
    >
      <Clock className="h-3.5 w-3.5" />
      {display}
    </button>
  );
}

// Inline editable mm:ss warmup chip shown beside the combined-warmup
// link icon on the leader event of a combined group. Click to edit;
// blur or Enter commits, Escape cancels.
function CombinedWarmupChip({ event, onSave, groupInfo }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const groupId = event.combined_warmup_group_id;
  const currentSeconds = event.warmup_seconds || 0;
  const display = `${Math.floor(currentSeconds / 60)}:${String(currentSeconds % 60).padStart(2, '0')}`;
  const tooltip = groupInfo
    ? `Combined warm-up: ${display} for ${groupInfo.skaterCount} skater${groupInfo.skaterCount === 1 ? '' : 's'} across ${groupInfo.label} — click to edit`
    : 'Combined warm-up duration — click to edit';

  const beginEdit = (e) => {
    e.stopPropagation();
    setDraft(display);
    setEditing(true);
  };

  const commit = async () => {
    const match = (draft || '').trim().match(/^(\d{1,3}):(\d{2})$/);
    if (!match) {
      toast.error('Enter warm-up as m:ss (e.g. 2:00)');
      setEditing(false);
      return;
    }
    const seconds = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
    if (seconds === currentSeconds) {
      setEditing(false);
      return;
    }
    setEditing(false);
    if (onSave) await onSave(groupId, seconds);
  };

  if (editing) {
    return (
      <input
        type="text"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-14 px-1 py-0 text-xs border border-emerald-400 rounded bg-white"
        placeholder="m:ss"
        data-testid={`combined-warmup-input-${event.programme_id}`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={beginEdit}
      title={tooltip}
      className="px-1.5 py-0.5 text-[11px] font-mono bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded transition-colors"
      data-testid={`combined-warmup-chip-${event.programme_id}`}
    >
      WU {display}
    </button>
  );
}

// Sortable event row component
function SortableEventRow({ event, index, showSchedule, onPanelChange, onClashClick, onChangeDayClick, competitionDays, breakTypes, onBreakTypeChange, onDeleteBreak, onUpdateBreakDuration, isSelected, onToggleSelect, onContextMenuOpen, onUncombine, onUpdateCombinedWarmup, combinedGroupLabel, combinedGroupInfo, isOOSLocked }) {
  const isFixedPosition = event.fixed_position === 'start' || event.fixed_position === 'end';
  const isBreak = event.is_break && !isFixedPosition;  // Regular break (not Doors Open/End of Day)
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ 
    id: event.programme_id,
    // Disable drag when this is a fixed-position row (Doors Open / End
    // of Day) OR when the OOS is owner-locked. Locked OOS short-circuits
    // every editing surface — see the toolbar disable + context-menu
    // gate too. Drag listeners get nulled below so a click-and-hold on
    // the row also won't initiate a drag preview.
    disabled: isFixedPosition || isOOSLocked
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isGroupByGrade = event.group_by_grade;
  const isRound2 = event.round === 2;
  const isShowcase = event.showcase_event;
  const hasClash = event.clash && !event.clash_cleared;
  const clashCleared = event.clash && event.clash_cleared;
  
  const panelOptions = ['', 'A', 'B', 'C'];
  
  const handlePanelChange = (newValue) => {
    onPanelChange(event.programme_id, newValue || null);
  };

  // Special rendering for Doors Open / End of Day markers
  if (isFixedPosition) {
    const isDoorsOpen = event.fixed_position === 'start';
    return (
      <tr 
        ref={setNodeRef} 
        style={style} 
        className={`border-b border-stone-200 ${
          isDoorsOpen ? 'bg-emerald-50' : 'bg-slate-100'
        }`}
        data-testid={`marker-row-${event.programme_id}`}
      >
        <td className="w-8 py-2 px-2">
          {/* No drag handle for fixed items */}
          <div className="w-4 h-4" />
        </td>
        <td className="py-2 px-2 text-center font-mono text-sm text-stone-400">
          {/* No event number */}
          -
        </td>
        {showSchedule && (
          <>
            <td className="py-2 px-2 text-center">
              <button
                className="group flex items-center gap-1 px-1.5 py-0.5 bg-stone-100 text-stone-700 text-xs rounded font-medium cursor-default"
                disabled
              >
                Day {event.day}
                <ArrowRightLeft className="h-3 w-3 opacity-0" />
              </button>
            </td>
            <td className="py-2 px-2 text-center text-sm font-semibold text-stone-700">
              {event.start_time || '-'}
            </td>
          </>
        )}
        <td className="py-2 px-2">
          <span className={`font-semibold ${isDoorsOpen ? 'text-emerald-700' : 'text-slate-600'}`}>
            {event.display_text || event.discipline_name}
          </span>
        </td>
        <td className="py-2 px-2 text-stone-700">
          {/* Grade - empty for markers */}
        </td>
        <td className="py-2 px-2 text-stone-500 text-sm">
          {/* Description - empty for markers */}
        </td>
        <td className="py-2 px-2 text-center">
          {/* No round */}
        </td>
        <td className="py-2 px-2 text-center">
          {/* No skaters */}
        </td>
        <td className="py-2 px-2 text-center">
          <div className="flex items-center justify-center gap-1 text-sm text-stone-500">
            <Clock className="h-3.5 w-3.5" />
            {formatDuration(event.event_time_estimated_seconds)}
          </div>
        </td>
        <td className="py-2 px-2 text-center">
          {/* No panels */}
        </td>
        <td className="py-2 px-2 text-center">
          {/* No clash indicator */}
        </td>
        <td className="w-10 py-2 px-2 text-center">
          {/* No actions for fixed markers */}
        </td>
      </tr>
    );
  }

  // Special rendering for regular breaks (not Doors Open / End of Day)
  if (isBreak) {
    const isPanelBlockBreak = event.break_scope === 'panel_block';
    const panelLetter = (event.panel || '').toUpperCase();
    return (
      <tr
        ref={setNodeRef}
        style={style}
        className={`border-b ${
          isPanelBlockBreak
            ? 'border-indigo-200 bg-indigo-50/60 hover:bg-indigo-100/60'
            : 'border-stone-200 bg-amber-50/50 hover:bg-amber-100/50'
        }`}
        data-testid={`break-row-${event.programme_id}`}
      >
        <td className="w-8 py-2 px-2">
          <button {...attributes} {...listeners} className="cursor-grab hover:text-green-700">
            <GripVertical className="h-4 w-4 text-stone-400" />
          </button>
        </td>
        <td className="py-2 px-2 text-center font-mono text-sm text-stone-400">
          -
        </td>
        {showSchedule && (
          <>
            <td className="py-2 px-2 text-center">
              <button
                className="group flex items-center gap-1 px-1.5 py-0.5 bg-stone-100 text-stone-700 text-xs rounded font-medium cursor-default"
                disabled
              >
                Day {event.day}
                <ArrowRightLeft className="h-3 w-3 opacity-0" />
              </button>
            </td>
            <td className="py-2 px-2 text-center text-sm text-stone-600">
              {event.start_time || '-'}
            </td>
          </>
        )}
        <td className="py-2 px-2">
          <div className="flex items-center gap-2">
            <select
              value={event.break_type || ''}
              onChange={(e) => onBreakTypeChange(event.programme_id, e.target.value)}
              className={`text-sm border rounded px-2 py-1 cursor-pointer focus:outline-none focus:ring-2 ${
                isPanelBlockBreak
                  ? 'border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 focus:ring-indigo-400'
                  : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 focus:ring-amber-400'
              }`}
            >
              {breakTypes.filter(bt => bt.break_type !== 'Doors Open' && bt.break_type !== 'End of Day').map(bt => (
                <option key={bt.break_id} value={bt.break_type}>{bt.break_type}</option>
              ))}
            </select>
            {isPanelBlockBreak && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-300"
                title="Linked mid-panel break — paired with corresponding breaks on the other panels"
                data-testid={`panel-break-badge-${event.programme_id}`}
              >
                <Layers className="h-3 w-3" />
                Linked
              </span>
            )}
          </div>
        </td>
        <td className="py-2 px-2 text-stone-500" />
        <td className="py-2 px-2 text-stone-600 text-sm italic">
          {event.display_text || ''}
        </td>
        <td className="py-2 px-2 text-center" />
        <td className="py-2 px-2 text-center" />
        <td className="py-2 px-2 text-center">
          <BreakDurationEditor event={event} onSave={onUpdateBreakDuration} disabled={isOOSLocked} />
        </td>
        <td className="py-2 px-2 text-center">
          {panelLetter ? (
            <span
              className={`px-2 py-0.5 text-xs font-semibold rounded ${
                panelLetter === 'A' ? 'bg-green-50 border border-green-300 text-green-700' :
                panelLetter === 'B' ? 'bg-orange-50 border border-orange-300 text-orange-700' :
                panelLetter === 'C' ? 'bg-blue-50 border border-blue-300 text-blue-700' :
                'bg-stone-100 text-stone-600'
              }`}
              data-testid={`break-row-panel-${event.programme_id}`}
            >
              {panelLetter}
            </span>
          ) : (
            <span className="text-xs text-stone-400">-</span>
          )}
        </td>
        <td className="py-2 px-2 text-center">
          {event.clash && (
            <button
              onClick={() => onClashClick(event.programme_id, event.clash_cleared)}
              className={`p-1 rounded transition-colors ${
                clashCleared ? 'bg-green-100 hover:bg-green-200' : 'bg-red-100 hover:bg-red-200'
              }`}
              title={clashCleared ? 'Clash cleared - click to view details' : 'Clash detected - click to view details'}
            >
              <AlertTriangle className={`h-4 w-4 ${clashCleared ? 'text-green-600' : 'text-red-500'}`} />
            </button>
          )}
        </td>
        <td className="w-10 py-2 px-2 text-center">
          <button
            onClick={() => onDeleteBreak(event.programme_id)}
            className="p-1 hover:bg-amber-200 rounded transition-colors"
            title={isPanelBlockBreak ? 'Delete this break (linked breaks on other panels also removed)' : 'Delete break'}
          >
            <Trash2 className="h-4 w-4 text-[#D4A84B]" />
          </button>
        </td>
      </tr>
    );
  }

  // Regular event row
  const inCombinedGroup = !!event.combined_warmup_group_id;
  const isLocked = !!event.locked;
  return (
    <tr 
      ref={setNodeRef} 
      style={style} 
      onClick={(e) => {
        // Ctrl/Cmd-click toggles multi-selection; plain clicks are
        // ignored so the existing drag-handle/icon interactions still
        // work. We avoid Shift-click because it triggers native text
        // selection on the page.
        if ((e.ctrlKey || e.metaKey) && onToggleSelect) {
          e.preventDefault();
          onToggleSelect(event.programme_id);
        }
      }}
      onContextMenu={(e) => {
        // Open the controlled DropdownMenu at the click coordinates.
        // We preventDefault to suppress the browser's native context
        // menu and pass the row programme_id + click position up to
        // the parent so it can position the menu and act on the
        // (newly auto-selected, or existing) selection.
        e.preventDefault();
        if (isOOSLocked) {
          // OOS is locked — short-circuit before opening the menu so
          // none of the bulk edit actions are reachable. The toolbar
          // toggle is the only way back to an editable state.
          return;
        }
        if (onContextMenuOpen) {
          onContextMenuOpen(event.programme_id, e.clientX, e.clientY);
        }
      }}
      className={`border-b border-stone-100 hover:bg-stone-50 ${
        isGroupByGrade ? 'bg-amber-50/50' : ''
      } ${isRound2 ? 'bg-blue-50/30' : ''} ${isShowcase ? 'bg-purple-50/30' : ''} ${
        hasClash ? 'bg-red-50/50' : ''
      } ${isSelected ? 'outline outline-2 outline-emerald-500 outline-offset-[-2px] bg-emerald-50/40' : ''} ${
        isLocked ? 'ring-1 ring-amber-400/70' : ''
      }`}
      data-testid={`event-row-${event.programme_id}`}
    >
      <td className="w-8 py-2 px-2">
        <button {...attributes} {...listeners} className="cursor-grab hover:text-green-700">
          <GripVertical className="h-4 w-4 text-stone-400" />
        </button>
      </td>
      <td className="py-2 px-2 text-center font-mono text-sm text-stone-500">
        {event.event_number}
      </td>
      {showSchedule && (
        <>
          <td className="py-2 px-2 text-center">
            {event.day && competitionDays.length > 1 ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChangeDayClick(event);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                className="group flex items-center gap-1 px-1.5 py-0.5 bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs rounded font-medium transition-colors cursor-pointer"
                title="Click to change day"
              >
                Day {event.day}
                <ArrowRightLeft className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ) : event.day ? (
              <span className="px-1.5 py-0.5 bg-stone-100 text-stone-700 text-xs rounded font-medium">
                Day {event.day}
              </span>
            ) : null}
          </td>
          <td className="py-2 px-2 text-center text-sm text-stone-600">
            {event.start_time || '-'}
          </td>
        </>
      )}
      <td className="py-2 px-2">
        <div className="flex items-center gap-2">
          <span className="font-medium text-stone-800">
            {event.discipline_name}
            {event.male_only_combined && <span className="ml-1 text-stone-600">(M)</span>}
          </span>
          {isShowcase && (
            <Sparkles className="h-3.5 w-3.5 text-purple-500" title="Showcase Event" />
          )}
          {isLocked && (
            <Lock
              className="h-3.5 w-3.5 text-amber-600"
              title="Locked — Apply Schedule / Regenerate refuse to run while this event is locked"
              data-testid={`event-locked-icon-${event.programme_id}`}
            />
          )}
        </div>
      </td>
      <td className="py-2 px-2 text-stone-700">
        <div className="flex items-center gap-1.5">
          <span>{event.grade_name}</span>
          {inCombinedGroup && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onUncombine) onUncombine(event.combined_warmup_group_id);
              }}
              title={(() => {
                if (combinedGroupInfo && combinedGroupLabel) {
                  const wu = combinedGroupInfo.warmupSeconds || 0;
                  const wuStr = `${Math.floor(wu / 60)}:${String(wu % 60).padStart(2, '0')}`;
                  const sk = combinedGroupInfo.skaterCount;
                  return `Combined warm-up: ${wuStr} for ${sk} skater${sk === 1 ? '' : 's'} across ${combinedGroupLabel} — click to uncombine`;
                }
                return combinedGroupLabel
                  ? `Combined warm-up: ${combinedGroupLabel} — click to uncombine`
                  : 'Combined warm-up — click to uncombine';
              })()}
              className="text-emerald-600 hover:text-emerald-800"
              data-testid={`combined-warmup-icon-${event.programme_id}`}
            >
              <Link2 className="h-3.5 w-3.5" />
            </button>
          )}
          {inCombinedGroup && (event.warmup_seconds || 0) > 0 && (
            <CombinedWarmupChip
              event={event}
              onSave={onUpdateCombinedWarmup}
              groupInfo={combinedGroupInfo}
            />
          )}
        </div>
      </td>
      <td className="py-2 px-2 text-stone-600 text-sm">{event.description}</td>
      <td className="py-2 px-2 text-center">
        {isRound2 ? (
          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">R2</span>
        ) : (
          <span className="px-1.5 py-0.5 bg-stone-100 text-stone-600 text-xs rounded">R1</span>
        )}
      </td>
      <td className="py-2 px-2 text-center">
        <div className="flex items-center justify-center gap-1 text-sm text-stone-600">
          <Users className="h-3.5 w-3.5" />
          {event.number_of_skaters}
        </div>
      </td>
      <td className="py-2 px-2 text-center">
        <div className="flex items-center justify-center gap-1 text-sm text-stone-600">
          <Clock className="h-3.5 w-3.5" />
          {formatDuration(event.event_time_estimated_seconds)}
        </div>
      </td>
      <td className="py-2 px-2 text-center">
        {event.can_use_panels === false ? (
          <span className="text-xs text-stone-400">-</span>
        ) : (
          <>
            <select 
              value={event.panel || ''} 
              onChange={(e) => handlePanelChange(e.target.value)}
              data-testid={`panel-select-${event.programme_id}`}
              className={`text-xs border rounded px-2 py-1 cursor-pointer ${
                event.panel === 'A' ? 'bg-green-50 border-green-300 text-green-700' :
                event.panel === 'B' ? 'bg-orange-50 border-orange-300 text-orange-700' :
                event.panel === 'C' ? 'bg-blue-50 border-blue-300 text-blue-700' :
                'bg-white border-stone-200 text-stone-500'
              }`}
            >
              {panelOptions.map(p => (
                <option key={p} value={p}>{p || '-'}</option>
              ))}
            </select>
          </>
        )}
      </td>
      <td className="py-2 px-2 text-center">
        {event.clash && (
          <button
            onClick={() => onClashClick(event.programme_id, event.clash_cleared)}
            className={`p-1 rounded transition-colors ${
              clashCleared 
                ? 'bg-green-100 hover:bg-green-200' 
                : 'bg-red-100 hover:bg-red-200'
            }`}
            title={clashCleared ? 'Clash cleared - click to view details' : 'Clash detected - click to view details'}
          >
            <AlertTriangle className={`h-4 w-4 ${
              clashCleared ? 'text-green-600' : 'text-red-500'
            }`} />
          </button>
        )}
      </td>
      <td className="w-10 py-2 px-2 text-center">
        {/* No actions for regular events */}
      </td>
    </tr>
  );
}

export default function OrderOfSkatePage() {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  
  const [competition, setCompetition] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [applyingSchedule, setApplyingSchedule] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Day tabs state
  const [activeDay, setActiveDay] = useState('1');
  
  // Change day dialog state
  const [changeDayDialogOpen, setChangeDayDialogOpen] = useState(false);
  const [changeDayEvent, setChangeDayEvent] = useState(null);
  const [selectedNewDay, setSelectedNewDay] = useState(null);
  const [changingDay, setChangingDay] = useState(false);

  // Duplicate-grade guard state — surfaces collisions before we
  // generate the OOS so the owner can spot phantom grades left
  // behind by a schedule version change or dual-schedule attach.
  const [duplicateGradeGroups, setDuplicateGradeGroups] = useState([]);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  
  // Clash dialog state
  const [clashDialogOpen, setClashDialogOpen] = useState(false);
  const [clashDialogData, setClashDialogData] = useState(null);
  const [loadingClashDetails, setLoadingClashDetails] = useState(false);
  
  // Day start time overrides state
  const [dayStartOverrides, setDayStartOverrides] = useState({});
  const [savingDayStart, setSavingDayStart] = useState(false);

  // Shorten Warm-ups toggle (per-competition; lives on OOS planning doc)
  const [shortenWarmups, setShortenWarmups] = useState(false);
  const [savingShortenWarmups, setSavingShortenWarmups] = useState(false);
  // Schedule-level "Warm Up Options for Event" override. When set to
  // a fixed value (e.g. "3 minutes") it acts as a hard cap on every
  // warmup block and supersedes the Shorten Warm-ups option (which
  // is disabled in the UI while a cap is in effect).
  const [warmUpOption, setWarmUpOption] = useState('As per rules');
  const warmUpOverrideActive = Boolean(warmUpOption) && warmUpOption !== 'As per rules';
  const [recalculatingTimings, setRecalculatingTimings] = useState(false);
  // Multi-select state for combine-warmups action bar
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [combiningWarmups, setCombiningWarmups] = useState(false);
  // Transient "Time saved" feedback after a successful Recalculate.
  // Positive = time saved (shrunk); negative = grew. Cleared after a
  // few seconds so it stays out of the way once the user has seen it.
  const [timeSavedSeconds, setTimeSavedSeconds] = useState(null);
  
  // Panel violation dialog states
  const [panelEventMovedDialogOpen, setPanelEventMovedDialogOpen] = useState(false);
  const [nonPanelInPanelGroupDialogOpen, setNonPanelInPanelGroupDialogOpen] = useState(false);
  const [maleFemaleSeperatedDialogOpen, setMaleFemaleSeperatedDialogOpen] = useState(false);
  // Drag-out guard for mid-panel breaks. When the backend rejects an
  // update that moves a ``break_scope == "panel_block"`` break outside
  // its panel block, we show this dialog and revert the optimistic UI
  // state back to what was loaded from the server before the drag.
  const [panelBreakDragOutDialogOpen, setPanelBreakDragOutDialogOpen] = useState(false);
  // Pending Add-Break confirmation when one or more other panels has
  // no end-time within ±10 min of the source panel's pause. We hold
  // the proposed insertion plan + the out-of-tolerance panel names
  // so the user can choose "Source Panel only" or cancel.
  const [panelBreakToleranceDialog, setPanelBreakToleranceDialog] = useState(null);
  const [preDragEvents, setPreDragEvents] = useState(null);

  // OOS-wide lock toggle. When ``isOOSLocked`` is true, every mutating
  // UI surface (drag, context menu, Add Break, Detect Clashes,
  // Recalculate Timings, Save, inline editors) is disabled and the
  // matching backend endpoints return 423. The unlock direction
  // requires a one-time critical-warning acknowledgement —
  // ``unlockWarningOpen`` toggles that dialog.
  const [isOOSLocked, setIsOOSLocked] = useState(false);
  const [lockToggling, setLockToggling] = useState(false);
  const [unlockWarningOpen, setUnlockWarningOpen] = useState(false);
  
  // Break types state
  const [breakTypes, setBreakTypes] = useState([]);
  const [editingDoorsOpen, setEditingDoorsOpen] = useState(null); // local value while editing
  const [addBreakType, setAddBreakType] = useState('');
  const [addBreakAfterSeq, setAddBreakAfterSeq] = useState('');
  const [addBreakOpen, setAddBreakOpen] = useState(false);
  // Override length (h:mm) for the new break. Prefilled from
  // the break type's "Default Length" when the type is selected.
  // Empty string means "use type default".
  const [addBreakLengthOverride, setAddBreakLengthOverride] = useState('');
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before starting drag
      },
    }),
    useSensor(KeyboardSensor)
  );
  
  const fetchCompetition = async () => {
    try {
      const res = await axios.get(`${API}/competitions/${competitionId}`, { withCredentials: true });
      setCompetition(res.data);
    } catch (error) {
      toast.error('Failed to load competition');
    }
  };
  
  const fetchOrderOfSkate = async () => {
    try {
      const res = await axios.get(`${API}/order-of-skate/${competitionId}`, { withCredentials: true });
      if (res.data.exists) {
        setOrderData(res.data);
        setEvents(res.data.events || []);
        setDayStartOverrides(res.data.day_start_overrides || {});
      } else {
        setOrderData(null);
        setEvents([]);
        setDayStartOverrides({});
      }
    } catch (error) {
      console.error('Failed to load order of skate:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBreakTypes = async () => {
    try {
      const res = await axios.get(`${API}/competition-breaks`, { withCredentials: true });
      setBreakTypes(res.data || []);
    } catch (error) {
      console.error('Failed to load break types:', error);
    }
  };

  // Pull the per-competition Shorten Warm-ups flag so the checkbox
  // reflects the current saved state on page load.
  const fetchShortenWarmups = async () => {
    try {
      const res = await axios.get(
        `${API}/order-of-skate/${competitionId}/planning-settings`,
        { withCredentials: true }
      );
      setShortenWarmups(Boolean(res.data?.shorten_warmups));
      if (res.data?.warm_up_option) {
        setWarmUpOption(res.data.warm_up_option);
      }
    } catch (error) {
      console.error('Failed to load shorten warmups flag:', error);
    }
  };

  const handleShortenWarmupsChange = async (checked) => {
    const next = Boolean(checked);
    setShortenWarmups(next);
    setSavingShortenWarmups(true);
    try {
      await axios.patch(
        `${API}/order-of-skate/${competitionId}/shorten-warmups`,
        { shorten_warmups: next },
        { withCredentials: true }
      );
      toast.success(
        next
          ? 'Shorten warm-ups enabled — click Recalculate Timings to apply'
          : 'Shorten warm-ups disabled — click Recalculate Timings to apply'
      );
    } catch (error) {
      // Roll back on failure
      setShortenWarmups(!next);
      toast.error('Failed to update Shorten warm-ups');
    } finally {
      setSavingShortenWarmups(false);
    }
  };

  const handleRecalculateTimings = async () => {
    setRecalculatingTimings(true);
    try {
      const res = await axios.post(
        `${API}/order-of-skate/${competitionId}/recalculate-timings`,
        {},
        { withCredentials: true }
      );
      await fetchOrderOfSkate();
      const updated = res.data?.events_updated ?? 0;
      const before = res.data?.total_estimated_time_seconds_before ?? 0;
      const after = res.data?.total_estimated_time_seconds_after ?? 0;
      const saved = before - after;
      if (before > 0 && saved !== 0) {
        setTimeSavedSeconds(saved);
        // Auto-clear after 8s so the badge doesn't linger
        setTimeout(() => setTimeSavedSeconds(null), 8000);
      } else {
        setTimeSavedSeconds(null);
      }
      toast.success(`Timings recalculated for ${updated} event${updated === 1 ? '' : 's'}`);
    } catch (error) {
      toast.error('Failed to recalculate timings');
      console.error(error);
    } finally {
      setRecalculatingTimings(false);
    }
  };

  const handleToggleEventSelect = (programmeId) => {
    setSelectedEventIds((prev) =>
      prev.includes(programmeId)
        ? prev.filter((id) => id !== programmeId)
        : [...prev, programmeId]
    );
  };

  const clearSelection = () => setSelectedEventIds([]);

  // Controlled context-menu state — one menu shared across all rows,
  // positioned at the right-click coordinates. Avoids per-row Radix
  // Slot wrapping which interferes with dnd-kit's PointerSensor.
  const [contextMenuState, setContextMenuState] = useState({
    open: false,
    x: 0,
    y: 0,
  });

  // When a row is right-clicked, ensure it's part of the active
  // selection before the menu opens. Native context-menu pattern:
  // right-clicking an unselected row replaces the selection with just
  // that row; right-clicking a row that's already selected keeps the
  // existing multi-selection so the menu acts on all of them.
  const handleContextMenuOpen = (programmeId, x, y) => {
    setSelectedEventIds((prev) => (prev.includes(programmeId) ? prev : [programmeId]));
    setContextMenuState({ open: true, x, y });
  };

  const closeContextMenu = () => setContextMenuState((s) => ({ ...s, open: false }));

  // Stats for the action bar -- only need them when a selection exists.
  const selectedEventObjs = (orderData?.events || []).filter((e) =>
    selectedEventIds.includes(e.programme_id)
  );
  const selectedSkaterCount = selectedEventObjs.reduce(
    (s, e) => s + (e.number_of_skaters || 0),
    0
  );

  // Derived selection-state flags consumed by the right-click ContextMenu.
  // Computed against the live ``events`` array so each render reflects
  // the latest server state (showcase / locked / combined-group toggles).
  const selectionState = useMemo(() => {
    const objs = (events || []).filter((e) => selectedEventIds.includes(e.programme_id));
    if (objs.length === 0) {
      return { count: 0 };
    }
    const days = new Set(objs.map((e) => e.day).filter((d) => d !== null && d !== undefined));
    const single = objs[0];
    return {
      count: objs.length,
      isSingle: objs.length === 1,
      singleEvent: objs.length === 1 ? single : null,
      sameDay: days.size === 1 ? [...days][0] : null,
      anyShowcase: objs.some((e) => e.showcase_event),
      allShowcase: objs.every((e) => e.showcase_event),
      anyLocked: objs.some((e) => e.locked),
      allLocked: objs.every((e) => e.locked),
      anyInCombinedGroup: objs.some((e) => e.combined_warmup_group_id),
      adjacentInSameDay: (() => {
        // Selection is adjacent + same day + same discipline (mirrors
        // backend combine-warmups validation; gate the menu item).
        if (objs.length < 2) return false;
        if (days.size !== 1) return false;
        const ids = new Set(selectedEventIds);
        const indices = [];
        (events || []).forEach((e, i) => { if (ids.has(e.programme_id)) indices.push(i); });
        indices.sort((a, b) => a - b);
        for (let i = 1; i < indices.length; i++) {
          if (indices[i] !== indices[i - 1] + 1) return false;
        }
        const disciplines = new Set(objs.map((e) => (e.discipline_name || '').toLowerCase()));
        if (disciplines.size !== 1) return false;
        const totalSkaters = objs.reduce((s, e) => s + (e.number_of_skaters || 0), 0);
        if (totalSkaters > 6) return false;
        if (objs.some((e) => e.combined_warmup_group_id)) return false;
        return true;
      })(),
    };
  }, [events, selectedEventIds]);

  // Map of combined_warmup_group_id -> { label, skaterCount, warmupSeconds }
  // so each row's chain-link tooltip + warmup chip can name the grades
  // it's grouped with and surface the total combined skater count + the
  // leader's warm-up duration.
  const combinedGroupInfo = (() => {
    const groups = {};
    for (const e of orderData?.events || []) {
      const gid = e.combined_warmup_group_id;
      if (!gid) continue;
      if (!groups[gid]) groups[gid] = { grades: [], skaters: 0, warmup: 0 };
      if (e.grade_name && !groups[gid].grades.includes(e.grade_name)) {
        groups[gid].grades.push(e.grade_name);
      }
      groups[gid].skaters += (e.number_of_skaters || 0);
      // Leader is the only group member with warmup_seconds > 0.
      // Use max() so non-leader members (which carry 0) don't reset it.
      if ((e.warmup_seconds || 0) > groups[gid].warmup) {
        groups[gid].warmup = e.warmup_seconds || 0;
      }
    }
    const out = {};
    for (const gid of Object.keys(groups)) {
      out[gid] = {
        label: groups[gid].grades.join(', '),
        skaterCount: groups[gid].skaters,
        warmupSeconds: groups[gid].warmup,
      };
    }
    return out;
  })();
  // Back-compat alias for the existing chain-link tooltip code path.
  const combinedGroupLabels = Object.fromEntries(
    Object.entries(combinedGroupInfo).map(([k, v]) => [k, v.label])
  );

  const handleCombineWarmups = async () => {
    if (selectedEventIds.length < 2) return;
    setCombiningWarmups(true);
    try {
      await axios.post(
        `${API}/order-of-skate/${competitionId}/combine-warmups`,
        { event_ids: selectedEventIds },
        { withCredentials: true }
      );
      await fetchOrderOfSkate();
      toast.success('Warm-ups combined');
      clearSelection();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to combine warm-ups';
      toast.error(msg);
    } finally {
      setCombiningWarmups(false);
    }
  };

  const handleUncombineWarmups = async (groupId) => {
    if (!groupId) return;
    try {
      await axios.delete(
        `${API}/order-of-skate/${competitionId}/combine-warmups/${groupId}`,
        { withCredentials: true }
      );
      await fetchOrderOfSkate();
      toast.success('Warm-ups uncombined');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to uncombine warm-ups';
      toast.error(msg);
    }
  };

  const handleUpdateCombinedWarmup = async (groupId, warmupSeconds) => {
    if (!groupId) return;
    try {
      await axios.patch(
        `${API}/order-of-skate/${competitionId}/combine-warmups/${groupId}`,
        { warmup_seconds: warmupSeconds },
        { withCredentials: true }
      );
      await fetchOrderOfSkate();
      toast.success('Combined warm-up updated');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to update combined warm-up';
      toast.error(msg);
    }
  };

  const handleUpdateBreakDuration = async (programmeId, seconds) => {
    if (!programmeId) return;
    try {
      await axios.patch(
        `${API}/order-of-skate/${competitionId}/break-duration`,
        { programme_id: programmeId, seconds },
        { withCredentials: true }
      );
      await fetchOrderOfSkate();
      toast.success('Break length updated');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to update break length';
      toast.error(msg);
    }
  };

  // ============================================================
  // Context Menu bulk actions (operate on selectedEventIds)
  // ============================================================
  const handleBulkSetShowcase = async (showcase) => {
    if (selectedEventIds.length === 0) return;
    try {
      const res = await axios.patch(
        `${API}/order-of-skate/${competitionId}/events/bulk-showcase`,
        { event_ids: selectedEventIds, showcase },
        { withCredentials: true }
      );
      await fetchOrderOfSkate();
      toast.success(res.data?.message || (showcase ? 'Marked as showcase' : 'Showcase removed'));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update showcase');
    }
  };

  const handleBulkSetLocked = async (locked) => {
    if (selectedEventIds.length === 0) return;
    try {
      const res = await axios.patch(
        `${API}/order-of-skate/${competitionId}/events/bulk-lock`,
        { event_ids: selectedEventIds, locked },
        { withCredentials: true }
      );
      await fetchOrderOfSkate();
      toast.success(res.data?.message || (locked ? 'Events locked' : 'Events unlocked'));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update lock state');
    }
  };

  const handleBulkMoveToDay = async (newDay) => {
    if (selectedEventIds.length === 0) return;
    try {
      const res = await axios.post(
        `${API}/order-of-skate/${competitionId}/events/bulk-move-day`,
        { event_ids: selectedEventIds, new_day: newDay },
        { withCredentials: true }
      );
      await fetchOrderOfSkate();
      toast.success(res.data?.message || `Moved to Day ${newDay}`);
      clearSelection();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to move events');
    }
  };

  const handleBulkMoveToTimeSlot = async (timeSlot) => {
    if (selectedEventIds.length === 0) return;
    try {
      const res = await axios.post(
        `${API}/order-of-skate/${competitionId}/events/bulk-move-time-slot`,
        { event_ids: selectedEventIds, time_slot: timeSlot },
        { withCredentials: true }
      );
      await fetchOrderOfSkate();
      toast.success(res.data?.message || `Moved to ${timeSlot}`);
      clearSelection();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to move events');
    }
  };

  const handleBulkSplitWarmups = async () => {
    if (selectedEventIds.length === 0) return;
    try {
      const res = await axios.post(
        `${API}/order-of-skate/${competitionId}/events/bulk-uncombine-warmups`,
        { event_ids: selectedEventIds },
        { withCredentials: true }
      );
      await fetchOrderOfSkate();
      toast.success(res.data?.message || 'Combined warm-up split');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to split warm-ups');
    }
  };

  const handleCopyEventTimes = async () => {
    if (selectedEventIds.length === 0) return;
    const lines = (events || [])
      .filter((e) => selectedEventIds.includes(e.programme_id))
      .map((e) => {
        const t = e.start_time || '--:--';
        const disc = e.discipline_name || '';
        const grade = e.grade_name ? ` ${e.grade_name}` : '';
        const desc = e.description ? ` (${e.description})` : '';
        return `${t} — ${disc}${grade}${desc}`;
      });
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${lines.length} event time${lines.length === 1 ? '' : 's'} to clipboard`);
    } catch (err) {
      // Fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        toast.success(`Copied ${lines.length} event time${lines.length === 1 ? '' : 's'} to clipboard`);
      } catch (e2) {
        toast.error('Failed to copy to clipboard');
      }
      document.body.removeChild(ta);
    }
  };

  // Open Add Break dialog pre-populated to insert relative to the
  // single right-clicked event. Position: "before" inserts after the
  // previous event in the array (so the new break ends up just above
  // this one); "after" inserts directly after this event.
  const handleInsertBreakRelative = (event, position) => {
    if (!event) return;
    const seq = event.sequence;
    if (!seq && seq !== 0) return;
    let anchorSeq = seq;
    if (position === 'before') {
      // Find the previous non-fixed event sequence
      const idx = events.findIndex((e) => e.programme_id === event.programme_id);
      let prevSeq = null;
      for (let i = idx - 1; i >= 0; i--) {
        if (!events[i].fixed_position) {
          prevSeq = events[i].sequence;
          break;
        }
      }
      anchorSeq = prevSeq !== null ? prevSeq : seq;
    }
    setAddBreakAfterSeq(String(anchorSeq));
    setAddBreakOpen(true);
  };
  
  useEffect(() => {
    fetchCompetition();
    fetchOrderOfSkate();
    fetchBreakTypes();
    fetchShortenWarmups();
    fetchLockState();
  }, [competitionId]);

  // Pull the current OOS lock state on mount. Reads
  // ``competition_order_of_skate.oos_locked``; returns ``locked: false``
  // when no OOS exists yet (auto-generate hasn't run).
  const fetchLockState = async () => {
    try {
      const res = await axios.get(
        `${API}/order-of-skate/${competitionId}/lock-state`,
        { withCredentials: true }
      );
      setIsOOSLocked(Boolean(res.data?.locked));
    } catch (err) {
      // Non-fatal — leave the toggle "off" if the read fails so the
      // user can still edit the OOS. The backend guard catches abuse.
      console.warn('Could not fetch OOS lock state', err);
    }
  };

  // Persist a lock-state change to the backend. The unlock confirmation
  // dialog calls this with ``locked=false``; the toggle calls it
  // directly when locking. ``afterApply`` lets the caller close its
  // own dialog/toast on success.
  const setLockStateOnServer = async (locked, afterApply) => {
    setLockToggling(true);
    try {
      const res = await axios.patch(
        `${API}/order-of-skate/${competitionId}/lock-state`,
        { locked: Boolean(locked) },
        { withCredentials: true }
      );
      setIsOOSLocked(Boolean(res.data?.locked));
      if (afterApply) afterApply();
      toast.success(
        locked
          ? 'Order of Skate is now locked — no edits allowed until unlocked'
          : 'Order of Skate unlocked — you can now make changes'
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update lock state');
    } finally {
      setLockToggling(false);
    }
  };

  // FE handler for the Switch's onCheckedChange. Locking is immediate
  // (no confirmation); unlocking opens the critical-warning dialog and
  // only proceeds after the user clicks "Understood".
  const handleLockToggle = (checked) => {
    if (checked) {
      void setLockStateOnServer(true);
    } else {
      setUnlockWarningOpen(true);
    }
  };
  
  // Auto-generate when page loads with no existing order of skate
  useEffect(() => {
    const autoGenerate = async () => {
      if (!loading && !orderData && events.length === 0 && !generating) {
        // Pre-flight duplicate-grade guard — same as the manual
        // Generate flow. Skip auto-generate and prompt the owner if
        // any duplicates are present.
        try {
          const dup = await axios.get(
            `${API}/order-of-skate/${competitionId}/grade-name-duplicates`,
            { withCredentials: true }
          );
          if (dup.data?.has_duplicates) {
            setDuplicateGradeGroups(dup.data.duplicate_groups || []);
            setDuplicateDialogOpen(true);
            return;
          }
        } catch (err) {
          console.warn('Duplicate-grade pre-check failed:', err);
        }
        setGenerating(true);
        try {
          const res = await axios.post(
            `${API}/order-of-skate/generate/${competitionId}`,
            {},
            { withCredentials: true }
          );
          toast.success(
            `Auto-generated ${res.data.total_events} events from ${res.data.grades_found ?? '?'} grades found in entries`
          );
          await fetchOrderOfSkate();
          
          // Auto-detect clashes after generation
          try {
            const clashRes = await axios.post(
              `${API}/order-of-skate/${competitionId}/detect-clashes`,
              {},
              { withCredentials: true }
            );
            const clashingIds = new Set(clashRes.data.events_with_clashes || []);
            setEvents(prev => prev.map(e => ({
              ...e,
              clash: clashingIds.has(e.programme_id),
              clash_cleared: clashingIds.has(e.programme_id) ? e.clash_cleared : false
            })));
            if (clashRes.data.clashes_found > 0) {
              toast.warning(`${clashRes.data.clashes_found} clash(es) detected`);
            }
          } catch (clashError) {
            console.error('Auto-detect clashes failed:', clashError);
          }
        } catch (error) {
          // Silent fail - user can manually generate
          console.error('Auto-generate failed:', error);
        } finally {
          setGenerating(false);
        }
      }
    };
    
    autoGenerate();
  }, [loading, orderData, competitionId]);
  
  const handleGenerate = async () => {
    // Pre-flight — surface any duplicate-named grades (same discipline
    // + name across different grade_ids) before we generate. See
    // /api/order-of-skate/{id}/grade-name-duplicates for context.
    try {
      const dup = await axios.get(
        `${API}/order-of-skate/${competitionId}/grade-name-duplicates`,
        { withCredentials: true }
      );
      if (dup.data?.has_duplicates) {
        setDuplicateGradeGroups(dup.data.duplicate_groups || []);
        setDuplicateDialogOpen(true);
        return; // wait for owner to confirm or cancel
      }
    } catch (err) {
      // If the pre-check fails we don't block generation — the
      // generator itself is still safe, we just lose the warning.
      console.warn('Duplicate-grade pre-check failed:', err);
    }
    await runGenerate();
  };

  const runGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/order-of-skate/generate/${competitionId}`,
        {},
        { withCredentials: true }
      );
      toast.success(
        `Generated ${res.data.total_events} events from ${res.data.grades_found ?? '?'} grades found in entries`
      );
      await fetchOrderOfSkate();
      
      // Auto-detect clashes after generation
      try {
        const clashRes = await axios.post(
          `${API}/order-of-skate/${competitionId}/detect-clashes`,
          {},
          { withCredentials: true }
        );
        const clashingIds = new Set(clashRes.data.events_with_clashes || []);
        setEvents(prev => prev.map(e => ({
          ...e,
          clash: clashingIds.has(e.programme_id),
          clash_cleared: clashingIds.has(e.programme_id) ? e.clash_cleared : false
        })));
        if (clashRes.data.clashes_found > 0) {
          toast.warning(`${clashRes.data.clashes_found} clash(es) detected`);
        }
      } catch (clashError) {
        console.error('Auto-detect clashes failed:', clashError);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate order of skate');
    } finally {
      setGenerating(false);
    }
  };
  
  const handleApplySchedule = async () => {
    setApplyingSchedule(true);
    try {
      const res = await axios.post(
        `${API}/order-of-skate/${competitionId}/apply-schedule`,
        {},
        { withCredentials: true }
      );
      toast.success(`Schedule applied: ${res.data.competition_days} day(s), ${res.data.panels_used?.length || 0} panels`);
      fetchOrderOfSkate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply schedule');
    } finally {
      setApplyingSchedule(false);
    }
  };
  
  const handleDownloadWorksheet = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/order-of-skate/${competitionId}/timing-worksheet`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TimingCalculations_${competition?.name?.replace(/\s+/g, '_') || 'Competition'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Timing worksheet downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download worksheet');
    } finally {
      setDownloading(false);
    }
  };
  
  const [downloadingOOS, setDownloadingOOS] = useState(false);
  
  const handleDownloadOOSDocument = async () => {
    setDownloadingOOS(true);
    try {
      const response = await axios.get(
        `${API}/order-of-skate/${competitionId}/oos-document`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `OrderOfSkate_${competition?.name?.replace(/\s+/g, '_') || 'Competition'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Order of Skate document downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download Order of Skate');
    } finally {
      setDownloadingOOS(false);
    }
  };
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    // Belt + braces: useSortable's ``disabled: isOOSLocked`` already
    // suppresses drag initiation, but if a drag somehow lands here
    // while locked we refuse to save and surface a toast.
    if (isOOSLocked) {
      toast.error('Order of Skate is locked — unlock it before reordering events');
      return;
    }

    // ``over`` is null when the pointer is released outside any valid
    // droppable (e.g. dropped on the page chrome). Bail out — there's
    // nothing to reorder and the rest of this handler assumes a target.
    if (!over) {
      return;
    }

    if (active.id !== over.id) {
      // Find the events being moved
      const activeEvent = events.find(e => e.programme_id === active.id);
      const overEvent = events.find(e => e.programme_id === over.id);
      
      // Prevent dragging fixed position items (Doors Open, End of Day)
      if (activeEvent?.fixed_position || overEvent?.fixed_position) {
        toast.error("Cannot reorder Doors Open or End of Day markers");
        return;
      }
      
      const oldIndex = events.findIndex(e => e.programme_id === active.id);
      const newIndex = events.findIndex(e => e.programme_id === over.id);
      
      const reordered = arrayMove(events, oldIndex, newIndex);

      // If the user is dragging a mid-panel break itself, pre-flight
      // validate the new position. If the break would land outside its
      // panel block, revert and show the AlertDialog WITHOUT saving —
      // we only want the dialog on intentional break drags, not on
      // every save where the block has been disrupted by other moves.
      if (activeEvent?.is_break && activeEvent?.break_scope === 'panel_block') {
        const newIdxAfterMove = reordered.findIndex(e => e === activeEvent);
        const panelBlockSigLocal = (ev) => {
          if (!ev || ev.is_break || ev.fixed_position) return null;
          if (ev.figure_panel_group) return `fig|${ev.day}`;
          if (ev.panel_group) return `pg|${ev.day}|${ev.panel_group}`;
          if (ev.group_by_grade && ev.panel) return `gbg|${ev.day}|${ev.grade_id}`;
          return null;
        };
        const prevSig = panelBlockSigLocal(reordered[newIdxAfterMove - 1]);
        const nextSig = panelBlockSigLocal(reordered[newIdxAfterMove + 1]);
        if (prevSig === null || prevSig !== nextSig) {
          setPanelBreakDragOutDialogOpen(true);
          return;
        }
      }
      
      // Save pre-drag state in case we need to revert (e.g., M/F separation cancel)
      setPreDragEvents(events);
      
      // Renumber events
      let eventNum = 1;
      reordered.forEach((e, idx) => {
        e.sequence = idx + 1;
        e.programme_id = idx + 1;
        if (!e.is_break) {
          e.event_number = eventNum++;
        }
      });
      
      setEvents(reordered);
      
      // Auto-save after drag
      setSaving(true);
      try {
        const res = await axios.put(
          `${API}/order-of-skate/${competitionId}`,
          { events: reordered },
          { withCredentials: true }
        );
        
        // Check for panel violation flags and show appropriate dialogs
        if (res.data.panel_event_moved_to_different_panel) {
          setPanelEventMovedDialogOpen(true);
        } else if (res.data.non_panel_event_in_panel_group) {
          setNonPanelInPanelGroupDialogOpen(true);
        } else if (res.data.male_female_separated) {
          setMaleFemaleSeperatedDialogOpen(true);
        }
        
        // Update events from the server response (which has re-sorted events)
        if (res.data.events) {
          setEvents(res.data.events);
        } else {
          // Fallback: reload from server
          const orderRes = await axios.get(`${API}/order-of-skate/${competitionId}`, { withCredentials: true });
          if (orderRes.data.exists) {
            setOrderData(orderRes.data);
            setEvents(orderRes.data.events || []);
          }
        }
        
        // Surface any orphaned-panel-break auto-downgrades as info
        // toasts so the user knows a sync break became sequential.
        const downgradeMsgs = res.data?.panel_break_downgrade_messages || [];
        for (const msg of downgradeMsgs) {
          toast.info(msg, { duration: 7000 });
        }

        if (res.data.clashes_found > 0) {
          toast.info(`Timing updated. ${res.data.clashes_found} clash(es)`);
        } else if (!res.data.panel_event_moved_to_different_panel && !res.data.non_panel_event_in_panel_group && downgradeMsgs.length === 0) {
          toast.success('Event moved - timing updated');
        }
      } catch (error) {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        if (status === 400 && detail) {
          toast.error(detail, { duration: 7000 });
        } else {
          toast.error('Failed to save changes');
        }
      } finally {
        setSaving(false);
      }
    }
  };
  
  const handlePanelChange = async (programmeId, newPanel) => {
    try {
      await axios.patch(
        `${API}/order-of-skate/${competitionId}/event-panel`,
        { programme_id: programmeId, panel: newPanel },
        { withCredentials: true }
      );

      // Update local state and capture the day of the changed event so
      // we can summarise the new panel running-times for the user.
      const changedEvent = events.find((e) => e.programme_id === programmeId);
      const affectedDay = changedEvent?.day ?? null;
      const nextEvents = events.map((e) =>
        e.programme_id === programmeId
          ? { ...e, panel: newPanel, panel_override: true }
          : e
      );
      setEvents(nextEvents);

      // Sum estimated time per panel on the affected day (panels run
      // in parallel within a day, so each panel's total is its own
      // wall-clock duration). Format as e.g. "A: 2h30m, B: 2h10m".
      const formatMinutes = (mins) => {
        const m = Math.max(0, Math.round(mins));
        const h = Math.floor(m / 60);
        const r = m % 60;
        if (h && r) return `${h}h${String(r).padStart(2, '0')}m`;
        if (h) return `${h}h`;
        return `${r}m`;
      };
      const panelTotals = {};
      if (affectedDay !== null) {
        for (const e of nextEvents) {
          if (e.day !== affectedDay) continue;
          if (e.is_break || e.fixed_position) continue;
          if (!e.panel) continue;
          panelTotals[e.panel] = (panelTotals[e.panel] || 0)
            + ((e.event_time_estimated_seconds || 0) / 60);
        }
      }
      const summary = Object.keys(panelTotals)
        .sort()
        .map((p) => `${p}: ${formatMinutes(panelTotals[p])}`)
        .join(', ');

      const label = affectedDay !== null && summary
        ? `Panel updated — Day ${affectedDay} (${summary})`
        : 'Panel updated';
      toast.success(label);
    } catch (error) {
      toast.error('Failed to update panel');
    }
  };
  
  const handleBreakTypeChange = async (programmeId, newBreakType) => {
    // Find the break type details
    const breakTypeDetails = breakTypes.find(bt => bt.break_type === newBreakType);
    if (!breakTypeDetails) return;
    
    // Parse the length to seconds (h:mm format)
    let durationSeconds = 0;
    if (breakTypeDetails.length) {
      const parts = breakTypeDetails.length.split(':');
      if (parts.length === 2) {
        durationSeconds = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60;
      }
    }
    
    // Update local state immediately
    setEvents(prev => prev.map(e => 
      e.programme_id === programmeId 
        ? { 
            ...e, 
            break_type: newBreakType,
            discipline_name: newBreakType,
            display_text: breakTypeDetails.display_text || '',
            event_time_estimated_seconds: durationSeconds
          }
        : e
    ));
    
    // Save to backend
    setSaving(true);
    try {
      const updatedEvents = events.map(e => 
        e.programme_id === programmeId 
          ? { 
              ...e, 
              break_type: newBreakType,
              discipline_name: newBreakType,
              display_text: breakTypeDetails.display_text || '',
              event_time_estimated_seconds: durationSeconds
            }
          : e
      );
      
      await axios.put(
        `${API}/order-of-skate/${competitionId}`,
        { events: updatedEvents },
        { withCredentials: true }
      );
      
      // Refresh to get updated timings
      await fetchOrderOfSkate();
      toast.success('Break type updated');
    } catch (error) {
      toast.error('Failed to update break type');
      // Revert on error
      await fetchOrderOfSkate();
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteBreak = async (programmeId) => {
    // Find the target break — if it's a linked panel break, also
    // remove every other break sharing the same group so the timing
    // stays consistent.
    const target = events.find(e => e.programme_id === programmeId);
    const groupId = target?.panel_break_group_id;
    const updatedEvents = events.filter(e => {
      if (e.programme_id === programmeId) return false;
      if (groupId && e.panel_break_group_id === groupId) return false;
      return true;
    });
    
    // Renumber events
    let eventNum = 1;
    updatedEvents.forEach((e, idx) => {
      e.sequence = idx + 1;
      e.programme_id = idx + 1;
      if (!e.is_break) {
        e.event_number = eventNum++;
      }
    });
    
    setEvents(updatedEvents);
    
    // Save to backend
    setSaving(true);
    try {
      await axios.put(
        `${API}/order-of-skate/${competitionId}`,
        { events: updatedEvents },
        { withCredentials: true }
      );
      
      // Refresh to get updated timings
      await fetchOrderOfSkate();
      toast.success('Break removed');
    } catch (error) {
      toast.error('Failed to remove break');
      // Revert on error
      await fetchOrderOfSkate();
    } finally {
      setSaving(false);
    }
  };
  
  const handleDetectClashes = async () => {
    try {
      const res = await axios.post(
        `${API}/order-of-skate/${competitionId}/detect-clashes`,
        {},
        { withCredentials: true }
      );
      
      // Update events with clash flags
      const clashingIds = new Set(res.data.events_with_clashes);
      setEvents(prev => prev.map(e => ({
        ...e,
        clash: clashingIds.has(e.programme_id),
        clash_cleared: clashingIds.has(e.programme_id) ? e.clash_cleared : false
      })));
      
      if (res.data.clashes_found > 0) {
        toast.warning(`${res.data.clashes_found} clash(es) detected`);
      } else {
        toast.success('No clashes detected');
      }
    } catch (error) {
      toast.error('Failed to detect clashes');
    }
  };
  

  // Confirm "Source Panel only" from the tolerance dialog: re-issue
  // the insert request with ``source_panel_only=true`` so the backend
  // skips the linked-break creation and inserts ONE sequential break
  // in the source panel.
  const handleConfirmAddSourcePanelOnly = async () => {
    const pending = panelBreakToleranceDialog;
    setPanelBreakToleranceDialog(null);
    if (!pending) return;
    try {
      setSaving(true);
      const res = await axios.post(
        `${API}/order-of-skate/${competitionId}/insert-panel-break`,
        {
          after_programme_id: pending.afterProgrammeId,
          break_type: pending.breakType,
          duration_secs: pending.durationSecs,
          display_text: pending.displayText,
          source_panel_only: true,
        },
        { withCredentials: true }
      );
      if (res.data?.events) setEvents(res.data.events);
      setAddBreakOpen(false);
      setAddBreakType('');
      setAddBreakAfterSeq('');
      setAddBreakLengthOverride('');
      toast.success(
        `${pending.breakType} added on Panel ${pending.sourcePanel} only — add breaks on the other panels manually if needed.`
      );
    } catch (error) {
      console.error('Add break (source-only) error:', error);
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Failed to add break'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddBreak = async () => {
    if (!addBreakType || !addBreakAfterSeq) {
      toast.error('Please select a break type and position');
      return;
    }

    const breakTypeDetails = breakTypes.find(bt => bt.break_type === addBreakType);
    if (!breakTypeDetails) return;

    // Parse break duration — prefer the user's per-insertion override
    // when present (allows one-off variations from the configured
    // default without editing the type itself). The Competition
    // Breaks config and the override input are both ``h:mm`` —
    // 0:15 = 15 min, 1:00 = 1 hour, 1:30 = 90 min.
    let durationSecs = 900; // default 15 min
    const lengthStr = (addBreakLengthOverride || breakTypeDetails.length || '').trim();
    if (lengthStr) {
      const parts = lengthStr.split(':');
      if (parts.length === 2) {
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        if (!Number.isNaN(h) && !Number.isNaN(m) && m < 60) {
          durationSecs = h * 3600 + m * 60;
        }
      }
    }

    const afterSeq = parseInt(addBreakAfterSeq);
    const sourceEvent = events.find(e => e.sequence === afterSeq);
    if (!sourceEvent) return;
    const afterProgrammeId = sourceEvent.programme_id;

    // POST the insert intent to the backend. The backend decides
    // whether this is a panel-block break, picks the closest matching
    // event on each other panel using authoritative wall-clock times,
    // and either completes the insert or returns HTTP 409 with an
    // out-of-tolerance payload so the FE can pop the confirmation
    // dialog. See ``routes/oos/insert_break.py`` for the planner.
    try {
      setSaving(true);
      const res = await axios.post(
        `${API}/order-of-skate/${competitionId}/insert-panel-break`,
        {
          after_programme_id: afterProgrammeId,
          break_type: addBreakType,
          duration_secs: durationSecs,
          display_text: breakTypeDetails.display_text || '',
        },
        { withCredentials: true }
      );
      if (res.data?.events) setEvents(res.data.events);
      setAddBreakOpen(false);
      setAddBreakType('');
      setAddBreakAfterSeq('');
      setAddBreakLengthOverride('');
      if (res.data?.is_panel_block) {
        toast.success(`${addBreakType} added as a mid-panel break — panels will sync at the break.`);
      } else {
        toast.success(`${addBreakType} added successfully`);
      }
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      // 409 = out-of-tolerance — pop the confirmation dialog
      if (status === 409 && detail && typeof detail === 'object' && detail.status === 'out_of_tolerance') {
        setPanelBreakToleranceDialog({
          sourcePanel: detail.source_panel,
          outOfTolerance: (detail.out_of_tolerance || []).map(p => ({
            panel: p.panel,
            distanceMins: p.distance_mins,
            endMins: p.end_mins,
          })),
          // Echo state needed to re-issue with source_panel_only=true
          breakType: addBreakType,
          durationSecs,
          displayText: breakTypeDetails.display_text || '',
          afterProgrammeId,
        });
        return;
      }
      console.error('Add break error:', error);
      if (status === 400 && typeof detail === 'string') {
        toast.error(detail, { duration: 7000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to add break');
      }
    } finally {
      setSaving(false);
    }
  };

  
  const handleMaleFemaleSeperationCancel = async () => {
    setMaleFemaleSeperatedDialogOpen(false);
    if (preDragEvents) {
      // Revert to pre-drag state and save to backend
      setSaving(true);
      try {
        const res = await axios.put(
          `${API}/order-of-skate/${competitionId}`,
          { events: preDragEvents },
          { withCredentials: true }
        );
        if (res.data.events) {
          setEvents(res.data.events);
        }
        toast.info('Move cancelled - events restored');
      } catch (error) {
        toast.error('Failed to revert changes');
      } finally {
        setSaving(false);
        setPreDragEvents(null);
      }
    }
  };

  const handleClashClick = async (programmeId, isCleared) => {
    setLoadingClashDetails(true);
    setClashDialogOpen(true);
    
    try {
      const res = await axios.get(
        `${API}/order-of-skate/${competitionId}/event/${programmeId}/clash-details`,
        { withCredentials: true }
      );
      
      setClashDialogData(res.data);
    } catch (error) {
      toast.error('Failed to load clash details');
      setClashDialogOpen(false);
    } finally {
      setLoadingClashDetails(false);
    }
  };
  
  const handleClashClearedToggle = async (programmeId) => {
    try {
      const res = await axios.patch(
        `${API}/order-of-skate/${competitionId}/event/${programmeId}/clash-cleared`,
        {},
        { withCredentials: true }
      );
      
      // Update local state
      setEvents(prev => prev.map(e => 
        e.programme_id === programmeId 
          ? { ...e, clash_cleared: res.data.clash_cleared }
          : e
      ));
      
      // Update dialog data if open
      if (clashDialogData && clashDialogData.programme_id === programmeId) {
        setClashDialogData(prev => ({ ...prev, clash_cleared: res.data.clash_cleared }));
      }
      
      toast.success(res.data.clash_cleared ? 'Clash marked as cleared' : 'Clash unmarked');
      setClashDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update clash status');
    }
  };
  
  // Handle day start time change
  const handleDayStartChange = async (dayNum, newTime) => {
    setSavingDayStart(true);
    try {
      const res = await axios.patch(
        `${API}/order-of-skate/${competitionId}/day-start`,
        { day: dayNum, start_time: newTime },
        { withCredentials: true }
      );
      
      // Update local state
      setDayStartOverrides(prev => ({ ...prev, [dayNum]: newTime }));
      
      // Reload events to get recalculated times
      await fetchOrderOfSkate();
      
      toast.success(`Day ${dayNum} start time updated`);
    } catch (error) {
      toast.error('Failed to update day start time');
    } finally {
      setSavingDayStart(false);
    }
  };
  
  // Get doors open time for a day
  const getDoorsOpenTime = (dayNum) => {
    const doorsOpenEvent = events.find(e => e.day === dayNum && e.fixed_position === 'start');
    return doorsOpenEvent?.start_time || '07:00';
  };
  
  // Get end of day time for a day
  const getEndOfDayTime = (dayNum) => {
    const endOfDayEvent = events.find(e => e.day === dayNum && e.fixed_position === 'end');
    return endOfDayEvent?.start_time || '--:--';
  };
  
  // Stats
  const totalEvents = events.filter(e => !e.is_break).length;
  // Calculate total time accounting for parallel panels
  const calculateTotalTime = () => {
    let total = 0;
    
    // Group figure panel events by panel
    const figurePanelEvents = events.filter(e => e.figure_panel_group && !e.is_break);
    const figurePanelTimes = {};
    figurePanelEvents.forEach(e => {
      const panel = e.panel || 'A';
      figurePanelTimes[panel] = (figurePanelTimes[panel] || 0) + (e.event_time_estimated_seconds || 0);
    });
    // Add only the longest figure panel time
    const maxFigurePanelTime = Math.max(0, ...Object.values(figurePanelTimes));
    total += maxFigurePanelTime;
    
    // Group GbG panel events by panel
    const gbgPanelEvents = events.filter(e => e.group_by_grade && e.can_use_panels && e.panel && !e.is_break);
    const gbgPanelTimes = {};
    gbgPanelEvents.forEach(e => {
      const panel = e.panel;
      gbgPanelTimes[panel] = (gbgPanelTimes[panel] || 0) + (e.event_time_estimated_seconds || 0);
    });
    // Add only the longest GbG panel time
    const maxGbgPanelTime = Math.max(0, ...Object.values(gbgPanelTimes));
    total += maxGbgPanelTime;
    
    // Add all other events (non-panel)
    const otherEvents = events.filter(e => 
      !e.is_break && 
      !e.figure_panel_group && 
      !(e.group_by_grade && e.can_use_panels && e.panel)
    );
    otherEvents.forEach(e => {
      total += e.event_time_estimated_seconds || 0;
    });
    
    // Add break durations
    events.filter(e => e.is_break).forEach(e => {
      total += e.event_time_estimated_seconds || 0;
    });
    
    return total;
  };
  
  const totalTime = calculateTotalTime();
  const hasSchedule = orderData?.schedule_applied;
  const clashCount = events.filter(e => e.clash && !e.clash_cleared).length;
  
  // Calculate competition days from dates
  const competitionDays = useMemo(() => {
    if (!competition?.start_date || !competition?.end_date) return [];
    const start = new Date(competition.start_date);
    const end = new Date(competition.end_date);
    const days = [];
    let current = new Date(start);
    let dayNum = 1;
    while (current <= end) {
      days.push({
        dayNumber: dayNum,
        date: new Date(current),
        dateStr: current.toLocaleDateString('en-NZ', { weekday: 'short', month: 'short', day: 'numeric' })
      });
      current.setDate(current.getDate() + 1);
      dayNum++;
    }
    return days;
  }, [competition?.start_date, competition?.end_date]);
  
  // Filter events by active day tab
  const filteredEvents = useMemo(() => {
    if (activeDay === 'all') return events;
    const dayNum = parseInt(activeDay);
    return events.filter(e => e.day === dayNum);
  }, [events, activeDay]);
  
  // Get events count per day for tab badges
  const eventsPerDay = useMemo(() => {
    const counts = {};
    events.forEach(e => {
      if (e.day) {
        counts[e.day] = (counts[e.day] || 0) + 1;
      }
    });
    return counts;
  }, [events]);
  
  // Handle opening change day dialog
  const handleChangeDayClick = (event) => {
    setChangeDayEvent(event);
    // Pre-select the *other* day automatically when the competition only
    // has two days (e.g. a 2-day comp on Day 1 pre-selects Day 2). With
    // 3+ days we leave the picker empty so the user has to make a choice.
    const otherDays = competitionDays.filter(d => d.dayNumber !== event.day);
    if (otherDays.length === 1) {
      setSelectedNewDay(String(otherDays[0].dayNumber));
    } else {
      setSelectedNewDay(null);
    }
    setChangeDayDialogOpen(true);
  };
  
  // Handle confirming day change
  const handleConfirmDayChange = async () => {
    if (!changeDayEvent || !selectedNewDay) return;
    
    setChangingDay(true);
    try {
      const res = await axios.put(
        `${API}/order-of-skate/${competitionId}/event/${changeDayEvent.programme_id}/change-day`,
        { new_day: parseInt(selectedNewDay) },
        { withCredentials: true }
      );
      
      toast.success(`Event moved to Day ${selectedNewDay}`);
      setChangeDayDialogOpen(false);
      setChangeDayEvent(null);
      fetchOrderOfSkate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change event day');
    } finally {
      setChangingDay(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(
              `/documents?tab=order-programme&doc=order_of_skate&comp=${encodeURIComponent(competitionId)}`
            )}
            className="p-2"
            title="Back to Documents → Order of Events & Programme → Order of Skate"
            data-testid="oos-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#2D4A3E' }}>Order of Skate</h1>
            <p className="text-muted-foreground">{competition?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* OOS-wide lock toggle. Locking is one click; unlocking opens
              the critical-warning AlertDialog below so the user has to
              acknowledge the "advise clubs" reminder before edits are
              re-enabled. The toggle is hidden until the OOS exists
              (``orderData`` truthy) — there's nothing to lock before
              generation. */}
          {orderData && (
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                isOOSLocked
                  ? 'border-red-300 bg-red-50/70'
                  : 'border-stone-200 bg-stone-50/60'
              }`}
              data-testid="oos-lock-toggle-wrap"
              title={
                isOOSLocked
                  ? 'Order of Skate is locked — toggle off to unlock (a warning will appear)'
                  : 'Lock the Order of Skate to prevent any further edits before distribution'
              }
            >
              {isOOSLocked ? (
                <Lock className="h-4 w-4 text-red-600" />
              ) : (
                <LockOpen className="h-4 w-4 text-stone-500" />
              )}
              <span
                className={`text-sm font-medium ${
                  isOOSLocked ? 'text-red-700' : 'text-stone-600'
                }`}
              >
                {isOOSLocked ? 'Locked' : 'Unlocked'}
              </span>
              <Switch
                checked={isOOSLocked}
                onCheckedChange={handleLockToggle}
                disabled={lockToggling}
                data-testid="oos-lock-toggle"
                aria-label="Lock or unlock the Order of Skate"
              />
            </div>
          )}
          {saving && (
            <span className="text-sm text-blue-600 mr-2">Saving...</span>
          )}
          {orderData && (
            <>
              <Button 
                variant="outline" 
                onClick={handleDownloadOOSDocument}
                disabled={downloadingOOS}
                data-testid="create-oos-doc-btn"
              >
                <FileSpreadsheet className={`mr-2 h-4 w-4 ${downloadingOOS ? 'animate-pulse' : ''}`} />
                {downloadingOOS ? 'Creating...' : 'Create Order of Skate'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleDownloadWorksheet}
                disabled={downloading}
              >
                <FileSpreadsheet className={`mr-2 h-4 w-4 ${downloading ? 'animate-pulse' : ''}`} />
                {downloading ? 'Downloading...' : 'Timing Worksheet'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setAddBreakOpen(true)}
                disabled={isOOSLocked}
                title={isOOSLocked ? 'Order of Skate is locked — unlock to add breaks' : undefined}
                data-testid="add-break-btn"
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Break
              </Button>
              <Button 
                variant="outline" 
                onClick={handleDetectClashes}
                disabled={isOOSLocked}
                title={isOOSLocked ? 'Order of Skate is locked — unlock to detect clashes' : undefined}
                className={clashCount > 0 ? 'border-red-300 text-red-700 hover:bg-red-50' : ''}
              >
                <AlertTriangle className="mr-2 h-4 w-4" />
                Detect Clashes {clashCount > 0 && `(${clashCount})`}
              </Button>
              {/* Reapply Schedule removed (Feb 2026, owner sign-off): the
                  re-apply path discards manual drag-and-drop ordering and
                  panel placements, which is close enough to a full reset
                  that owners should use Documents → "Reset Order of Skate"
                  + Generate instead. The first-time Apply path is still
                  reachable via the amber "Schedule Not Applied" banner
                  below (only fires when ``orderData`` exists but
                  ``hasSchedule`` is false — rare, only when Generate
                  was called with auto_apply=False). Backend
                  ``/apply-schedule`` endpoint kept alive because
                  ``Generate`` still calls it for the initial placement. */}
              <Button 
                variant="outline" 
                onClick={handleRecalculateTimings}
                disabled={recalculatingTimings || isOOSLocked}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="recalculate-timings-btn"
                title={
                  isOOSLocked
                    ? 'Order of Skate is locked — unlock to recalculate timings'
                    : 'Refresh per-event durations using the current Event Timing rules and the Shorten Warm-ups toggle. Preserves the existing event order, day assignments and panel placements.'
                }
              >
                <Clock className={`mr-2 h-4 w-4 ${recalculatingTimings ? 'animate-spin' : ''}`} />
                {recalculatingTimings ? 'Recalculating...' : 'Recalculate Timings'}
              </Button>
            </>
          )}
          {!orderData && (
            <Button 
              variant="outline" 
              onClick={handleGenerate}
              disabled={generating}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
              Generate
            </Button>
          )}
        </div>
      </div>
      
      {/* Stats Bar - Only Total Events and Est. Total Time */}
      {orderData && (
        <div className="flex gap-4" style={{ width: '50%' }}>
          <Card className="p-4 flex-1">
            <div className="text-2xl font-bold text-stone-800">{totalEvents}</div>
            <div className="text-sm text-muted-foreground">Total Events</div>
          </Card>
          <Card className="p-4 flex-1 relative overflow-hidden">
            <div className="text-2xl font-bold text-green-700">{formatDuration(totalTime)}</div>
            <div className="text-sm text-muted-foreground">Est. Total Time</div>
            {timeSavedSeconds !== null && (
              <div
                className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold shadow-sm animate-in fade-in slide-in-from-top-1 duration-300 ${
                  timeSavedSeconds > 0
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    : 'bg-amber-100 text-amber-800 border border-amber-200'
                }`}
                data-testid="time-saved-badge"
                title="Difference vs. before Recalculate Timings"
              >
                {timeSavedSeconds > 0 ? '−' : '+'}{formatDuration(Math.abs(timeSavedSeconds))}
                <span className="font-normal opacity-80">
                  {timeSavedSeconds > 0 ? 'saved' : 'added'}
                </span>
              </div>
            )}
          </Card>
        </div>
      )}
      
      {/* Schedule Status Banner */}
      {orderData && !hasSchedule && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Schedule Not Applied</p>
                <p className="text-sm text-amber-600">
                  Click "Apply Schedule" to assign days, time blocks, and panels based on {competition?.competition_type || 'Regional'} settings.
                </p>
              </div>
            </div>
            <Button 
              onClick={handleApplySchedule}
              disabled={applyingSchedule}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Calendar className="mr-2 h-4 w-4" />
              Apply Schedule
            </Button>
          </div>
        </Card>
      )}
      
      {/* No Order Yet */}
      {!orderData && (
        <Card className="p-12 text-center">
          <Clock className="h-16 w-16 mx-auto text-stone-300 mb-4" />
          <h2 className="text-xl font-semibold text-stone-700 mb-2">No Order of Skate Generated</h2>
          <p className="text-muted-foreground mb-6">
            Generate the initial event order based on the grades selected for this competition.
          </p>
          <Button onClick={handleGenerate} disabled={generating} className="btn-primary">
            <RefreshCw className={`mr-2 h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
            Generate Order of Skate
          </Button>
        </Card>
      )}
      
      {/* Events Table */}
      {orderData && events.length > 0 && (
        <>
          {/* Day Tabs - only show for multi-day competitions */}
          {competitionDays.length > 1 && (
            <Tabs value={activeDay} onValueChange={setActiveDay} className="w-full">
              <TabsList className="w-full justify-start bg-stone-100 p-1 rounded-lg mb-4">
                {competitionDays.map(day => (
                  <TabsTrigger 
                    key={day.dayNumber} 
                    value={String(day.dayNumber)}
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  >
                    Day {day.dayNumber} - {day.dateStr} ({eventsPerDay[day.dayNumber] || 0})
                  </TabsTrigger>
                ))}
                <TabsTrigger 
                  value="all" 
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  All Days ({events.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          
          {/* Day Start/End Time Controls - show when viewing specific day */}
          {activeDay !== 'all' && hasSchedule && (
            <div className="flex items-center gap-6 mb-4 p-3 bg-stone-50 rounded-lg border border-stone-200">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-stone-600">Doors Open:</label>
                <input
                  type="time"
                  value={editingDoorsOpen !== null ? editingDoorsOpen : getDoorsOpenTime(parseInt(activeDay))}
                  onChange={(e) => setEditingDoorsOpen(e.target.value)}
                  onBlur={() => {
                    if (editingDoorsOpen !== null && editingDoorsOpen !== getDoorsOpenTime(parseInt(activeDay))) {
                      handleDayStartChange(parseInt(activeDay), editingDoorsOpen);
                    }
                    setEditingDoorsOpen(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    }
                  }}
                  onFocus={() => setEditingDoorsOpen(getDoorsOpenTime(parseInt(activeDay)))}
                  disabled={savingDayStart || isOOSLocked}
                  title={isOOSLocked ? 'Order of Skate is locked — unlock to change Doors Open' : undefined}
                  className="px-2 py-1 text-sm border border-stone-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {savingDayStart && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-stone-600">End of Day:</label>
                <span className="px-2 py-1 text-sm border border-stone-300 rounded bg-stone-50 text-stone-600">
                  {formatTimeTo12Hour(getEndOfDayTime(parseInt(activeDay)))}
                </span>
              </div>

              {/* Shorten Warm-ups toggle — applies competition-wide.
                  Disabled when a hard cap is set via Schedule Settings
                  > Warm Up Options for Event (the cap supersedes
                  shortening). */}
              <label
                className={`ml-auto flex items-center gap-2 text-sm font-medium select-none ${
                  warmUpOverrideActive
                    ? 'text-stone-400 cursor-not-allowed'
                    : 'text-stone-600 cursor-pointer'
                }`}
                title={
                  warmUpOverrideActive
                    ? `Disabled — a hard warm-up cap (${warmUpOption}) is set in Schedule Settings and supersedes this option.`
                    : "When enabled, grades flagged 'Can shorten' in Event Timing drop their '+ Add for warm-up' allowance. Grades whose base warm-up is already below 2 minutes are left untouched. Regenerate to apply."
                }
              >
                <input
                  type="checkbox"
                  checked={shortenWarmups && !warmUpOverrideActive}
                  onChange={(e) => handleShortenWarmupsChange(e.target.checked)}
                  disabled={savingShortenWarmups || warmUpOverrideActive}
                  className="h-4 w-4 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed"
                  data-testid="shorten-warmups-checkbox"
                />
                Shorten Warm-ups
                {savingShortenWarmups && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
              </label>
            </div>
          )}
          
          {/* Combine Warmups action bar -- shows when 2+ events are selected */}
          {selectedEventIds.length >= 1 && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="combine-warmups-action-bar">
              <Link2 className="h-4 w-4 text-emerald-700" />
              <span className="text-sm font-medium text-emerald-800">
                {selectedEventIds.length} event{selectedEventIds.length === 1 ? '' : 's'} selected · {selectedSkaterCount} skater{selectedSkaterCount === 1 ? '' : 's'}
              </span>
              <span className="text-xs text-emerald-700/70 ml-2">
                Ctrl/Cmd-click adjacent same-discipline events on the same day. Cap: 6 skaters.
              </span>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCombineWarmups}
                  disabled={selectedEventIds.length < 2 || selectedSkaterCount > 6 || combiningWarmups}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  data-testid="combine-warmups-btn"
                >
                  {combiningWarmups ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
                  Combine Warmups
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="text-stone-600 hover:text-stone-800"
                >
                  Clear
                </Button>
              </div>
            </div>
          )}

          <Card className="overflow-hidden">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-stone-200 bg-stone-50">
                      <th className="w-8 py-2 px-2"></th>
                      <th className="py-2 px-2 text-center text-xs font-semibold text-stone-600 uppercase">#</th>
                      {hasSchedule && (
                        <>
                          <th className="py-2 px-2 text-center text-xs font-semibold text-stone-600 uppercase">Day</th>
                          <th className="py-2 px-2 text-center text-xs font-semibold text-stone-600 uppercase">Time</th>
                        </>
                      )}
                      <th className="py-2 px-2 text-left text-xs font-semibold text-stone-600 uppercase">Discipline</th>
                      <th className="py-2 px-2 text-left text-xs font-semibold text-stone-600 uppercase">Grade</th>
                      <th className="py-2 px-2 text-left text-xs font-semibold text-stone-600 uppercase">Description</th>
                      <th className="py-2 px-2 text-center text-xs font-semibold text-stone-600 uppercase">Round</th>
                      <th className="py-2 px-2 text-center text-xs font-semibold text-stone-600 uppercase">Skaters</th>
                      <th className="py-2 px-2 text-center text-xs font-semibold text-stone-600 uppercase">Duration</th>
                      <th className="py-2 px-2 text-center text-xs font-semibold text-stone-600 uppercase">Panel</th>
                      <th className="py-2 px-2 text-center text-xs font-semibold text-stone-600 uppercase">Clash</th>
                      <th className="w-10 py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <SortableContext items={filteredEvents.map(e => e.programme_id)} strategy={verticalListSortingStrategy}>
                      {filteredEvents.map((event, index) => (
                        <SortableEventRow 
                          key={event.programme_id} 
                          event={event} 
                          index={index} 
                          showSchedule={hasSchedule}
                          onPanelChange={handlePanelChange}
                          onClashClick={handleClashClick}
                          onChangeDayClick={handleChangeDayClick}
                          competitionDays={competitionDays}
                          breakTypes={breakTypes}
                          onBreakTypeChange={handleBreakTypeChange}
                          onDeleteBreak={handleDeleteBreak}
                          isSelected={selectedEventIds.includes(event.programme_id)}
                          onToggleSelect={handleToggleEventSelect}
                          onContextMenuOpen={(event.is_break || event.fixed_position) ? undefined : handleContextMenuOpen}
                          onUncombine={handleUncombineWarmups}
                          onUpdateCombinedWarmup={handleUpdateCombinedWarmup}
                          onUpdateBreakDuration={handleUpdateBreakDuration}
                          combinedGroupLabel={combinedGroupLabels[event.combined_warmup_group_id] || null}
                          combinedGroupInfo={combinedGroupInfo[event.combined_warmup_group_id] || null}
                          isOOSLocked={isOOSLocked}
                        />
                      ))}
                    </SortableContext>
                  </tbody>
                </table>
              </div>
            </DndContext>
          </Card>
          
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-50 border border-amber-200 rounded"></div>
              <span>Group By Grade</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded"></div>
              <span>Round 2</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              <span>Showcase Event</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">A</span>
              <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">B</span>
              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">C</span>
              <span>Panels</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span>Clash (click to clear)</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-green-600" />
              <span>Clash Cleared</span>
            </div>
          </div>
        </>
      )}
      
      {/* Change Day Dialog */}
      <Dialog open={changeDayDialogOpen} onOpenChange={setChangeDayDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Change Event Day
            </DialogTitle>
          </DialogHeader>
          
          {changeDayEvent && (
            <div className="space-y-4">
              <div className="bg-stone-50 p-3 rounded border border-stone-200">
                <p className="font-medium text-stone-800">
                  {formatGradeLabel(changeDayEvent.grade_name, changeDayEvent.discipline_name, {
                    gender_split: changeDayEvent.gender_split,
                    male_only_combined: changeDayEvent.male_only_combined,
                  })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {changeDayEvent.description} (R{changeDayEvent.round})
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Currently on: Day {changeDayEvent.day}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Move to:</label>
                <Select value={selectedNewDay || ''} onValueChange={setSelectedNewDay}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a day" />
                  </SelectTrigger>
                  <SelectContent 
                    className="z-[9999]" 
                    position="popper" 
                    sideOffset={5}
                  >
                    {competitionDays.map(day => (
                      <SelectItem 
                        key={day.dayNumber} 
                        value={String(day.dayNumber)}
                        disabled={day.dayNumber === changeDayEvent?.day}
                      >
                        Day {day.dayNumber} - {day.dateStr}
                        {day.dayNumber === changeDayEvent?.day && ' (current)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedNewDay && (
                <p className="text-sm text-muted-foreground">
                  The event will be placed near other {changeDayEvent.discipline_name} events on Day {selectedNewDay}, 
                  or at the end of the afternoon session before the meal break.
                </p>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleConfirmDayChange}
              disabled={!selectedNewDay || changingDay}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {changingDay ? 'Moving...' : 'Confirm Move'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Clash Details Dialog */}
      <Dialog open={clashDialogOpen} onOpenChange={setClashDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${
                clashDialogData?.clash_cleared ? 'text-green-600' : 'text-red-500'
              }`} />
              Event Clash
            </DialogTitle>
          </DialogHeader>
          
          {loadingClashDetails ? (
            <div className="py-8 text-center text-muted-foreground">
              Loading clash details...
            </div>
          ) : clashDialogData ? (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  {clashDialogData.clash_cleared 
                    ? 'Potential clashes within this event:' 
                    : 'Clashes within this event:'}
                </p>
                
                {clashDialogData.clashes?.length > 0 ? (
                  <ul className="space-y-2 max-h-64 overflow-y-auto">
                    {clashDialogData.clashes.map((clash, idx) => (
                      <li key={idx} className="text-sm bg-stone-50 p-2 rounded border border-stone-200">
                        <span className="font-medium">{clash.skater_name}</span>
                        <span className="text-muted-foreground"> - </span>
                        <span>{clash.this_event} ({clash.this_event_time})</span>
                        <span className="text-red-500"> clashes with </span>
                        <span>{clash.clashes_with} ({clash.clashes_with_time})</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No clash details available</p>
                )}
              </div>
              
              {clashDialogData.clash_cleared && (
                <p className="text-sm text-green-700 bg-green-50 p-2 rounded border border-green-200">
                  This clash has been cleared already - do you want to change that?
                </p>
              )}
            </div>
          ) : null}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <DialogClose asChild>
              <Button variant="outline">
                Exit to manage clash
              </Button>
            </DialogClose>
            {clashDialogData && (
              <Button 
                onClick={() => handleClashClearedToggle(clashDialogData.programme_id)}
                className={clashDialogData.clash_cleared 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-green-600 hover:bg-green-700'}
              >
                {clashDialogData.clash_cleared ? 'Mark Clash Uncleared' : 'Mark Clash Cleared'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* OOS unlock warning — fires when the user flips the lock toggle OFF.
          The label/text/icon were specified by the owner (Feb 2026). The
          dialog can only be dismissed by clicking "Understood", which
          then calls the backend to actually unlock the OOS. Cancelling
          (Esc / overlay click) does NOT unlock — keeps the toggle on. */}
      <AlertDialog
        open={unlockWarningOpen}
        onOpenChange={(o) => { if (!o) setUnlockWarningOpen(false); }}
      >
        <AlertDialogContent data-testid="oos-unlock-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              You have unlocked the Order of Skate
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              You can now make changes to the order of skate &mdash; if a
              programme has already been issued you will need to advise
              all participating clubs of any changes you make at this
              time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() =>
                setLockStateOnServer(false, () => setUnlockWarningOpen(false))
              }
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="oos-unlock-understood-btn"
            >
              Understood
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Panel Event Moved to Different Panel Dialog */}
      <AlertDialog open={panelEventMovedDialogOpen} onOpenChange={setPanelEventMovedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Is this change meant to update events on this panel?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              You've moved an event from one panel to another. Please update the panel selection to finalise the change or the app will resort this back into its original panel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Non-Panel Event in Panel Group Dialog */}
      <AlertDialog open={nonPanelInPanelGroupDialogOpen} onOpenChange={setNonPanelInPanelGroupDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Event shifted into panel event time slot
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              You've moved an event which doesn't operate in panels into a time slot for panel events. This event's taken up position immediately after the panel events.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Male/Female Separated Dialog */}
      <AlertDialog open={maleFemaleSeperatedDialogOpen} onOpenChange={(open) => { if (!open) setMaleFemaleSeperatedDialogOpen(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Separating male and female grades
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              The initial settings for the order of skate required the male and female grades for the same event to be skated together. Separating them will break the formal link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleMaleFemaleSeperationCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setMaleFemaleSeperatedDialogOpen(false); setPreDragEvents(null); }}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mid-Panel Break Drag-Out Guard */}
      <AlertDialog open={panelBreakDragOutDialogOpen} onOpenChange={setPanelBreakDragOutDialogOpen}>
        <AlertDialogContent data-testid="panel-break-dragout-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Can&apos;t move this break
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              This is a mid-panel break — it pauses every panel in the block at the same wall-clock time. Moving it outside the panel block would desync the panels and corrupt the schedule.
              <br /><br />
              To relocate it: delete it from its current position, then add a new break at the spot you want.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="panel-break-dragout-ok" onClick={() => setPanelBreakDragOutDialogOpen(false)}>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Mid-Panel Break — tolerance confirmation */}
      <AlertDialog open={!!panelBreakToleranceDialog} onOpenChange={(o) => { if (!o) setPanelBreakToleranceDialog(null); }}>
        <AlertDialogContent data-testid="panel-break-tolerance-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              No matching pause within 10 minutes
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left" asChild>
              <div>
                <p>
                  Panel <strong>{panelBreakToleranceDialog?.sourcePanel}</strong> pauses at the chosen position, but the other panels don&apos;t have an event ending within ±10 minutes of that time:
                </p>
                <ul className="my-3 list-disc pl-5 text-sm">
                  {(panelBreakToleranceDialog?.outOfTolerance || []).map((p) => (
                    <li key={p.panel}>
                      Panel <strong>{p.panel}</strong>
                      {p.distanceMins !== null
                        ? ` — nearest pause is ${p.distanceMins} min away`
                        : ' — no events to pair with'}
                    </li>
                  ))}
                </ul>
                <p>
                  Adding linked breaks here would create a very uneven break length on those panels. You can add the break only on the source panel (the other panels keep running, you can add their breaks manually) — or cancel and try a different position.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="panel-break-tolerance-cancel"
              onClick={() => setPanelBreakToleranceDialog(null)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="panel-break-tolerance-source-only"
              onClick={() => handleConfirmAddSourcePanelOnly()}
            >
              Source Panel only
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate-grade guard — surfaces same-name/discipline grades
          across different grade_ids before the OOS is generated. */}
      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent className="max-w-lg" data-testid="duplicate-grade-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Duplicate grade names detected
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-left">
                <p>
                  The following grades resolve to the same name in your entries.
                  This usually happens when a schedule is updated after skaters
                  are entered, or when the same grade lives in both your main
                  schedule and a supplementary schedule.
                </p>
                <p>
                  Generating anyway will produce a <strong>separate event for each</strong>,
                  which is probably not what you want (that&apos;s exactly how the
                  &ldquo;6 on Panel A, 1 on Panel B&rdquo; split happens).
                </p>
                <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-3">
                  {duplicateGradeGroups.map((group) => (
                    <div key={group.display_name} data-testid={`dup-group-${group.display_name}`}>
                      <div className="text-sm font-semibold text-stone-800">
                        {group.display_name}
                      </div>
                      <ul className="mt-1 text-sm text-stone-700 space-y-0.5">
                        {group.grades.map((g) => (
                          <li key={g.grade_id}>
                            <span className="font-medium">{g.entry_count}</span>
                            {' '}entrant{g.entry_count === 1 ? '' : 's'} against schedule{' '}
                            <em>{g.schedule_name}</em>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="text-sm">
                  Recommendation: cancel, move the stray entries onto the intended
                  grade in Skaters &amp; Entries, then come back and generate.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dup-cancel">
              Cancel &amp; fix entries
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="dup-continue"
              onClick={() => {
                setDuplicateDialogOpen(false);
                runGenerate();
              }}
            >
              Generate anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Add Break Dialog */}
      <Dialog open={addBreakOpen} onOpenChange={(open) => { setAddBreakOpen(open); if (!open) { setAddBreakType(''); setAddBreakAfterSeq(''); setAddBreakLengthOverride(''); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Break</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Break Type</label>
              <select
                className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm"
                value={addBreakType}
                onChange={(e) => {
                  const v = e.target.value;
                  setAddBreakType(v);
                  // Prefill the length override with the type's
                  // configured default — user can edit if they want
                  // a one-off variation for this insertion.
                  const bt = breakTypes.find(b => b.break_type === v);
                  setAddBreakLengthOverride(bt?.length || '');
                }}
                data-testid="add-break-type-select"
              >
                <option value="">Select break type...</option>
                {breakTypes
                  .filter(bt => bt.break_type !== 'Doors Open' && bt.break_type !== 'End of Day')
                  .map(bt => (
                    <option key={bt.break_id} value={bt.break_type}>
                      {bt.break_type} ({bt.length || '0:15'})
                    </option>
                  ))}
              </select>
            </div>
            {addBreakType && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Length (h:mm)</label>
                <input
                  type="text"
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm"
                  value={addBreakLengthOverride}
                  onChange={(e) => setAddBreakLengthOverride(e.target.value)}
                  placeholder="e.g. 0:15 or 1:00"
                  data-testid="add-break-length-input"
                />
                <p className="text-xs text-stone-500">
                  Prefilled from the break type&apos;s default — change here for a one-off variation.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Insert after event</label>
              <div className="max-h-[45vh] overflow-y-auto border rounded-md">
                {events
                  .filter(e => !e.fixed_position)
                  .map(e => {
                    const isSelected = addBreakAfterSeq === String(e.sequence);
                    const isBreak = e.is_break;
                    return (
                      <div
                        key={e.sequence}
                        className={`flex items-center px-3 py-1.5 cursor-pointer border-b border-stone-100 text-sm transition-colors
                          ${isSelected ? 'bg-green-100 border-l-4 border-l-green-600' : 'hover:bg-stone-50'}
                          ${isBreak ? 'italic text-amber-700 bg-amber-50/50' : ''}`}
                        onClick={() => setAddBreakAfterSeq(String(e.sequence))}
                        data-testid={`add-break-position-${e.sequence}`}
                      >
                        <span className="w-6 text-stone-400 text-xs mr-2">
                          {!isBreak ? e.event_number : ''}
                        </span>
                        <span className="w-12 text-stone-500 text-xs mr-2">{e.start_time}</span>
                        <span className="flex-1">
                          {isBreak ? e.break_type : formatGradeLabel(e.grade_name, e.discipline_name, {
                            gender_split: e.gender_split,
                            male_only_combined: e.male_only_combined,
                          })}
                        </span>
                        {e.panel && <span className="text-xs text-stone-400 ml-2">P{e.panel}</span>}
                        <span className="text-xs text-stone-400 ml-2">D{e.day}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleAddBreak}
              disabled={!addBreakType || !addBreakAfterSeq}
              className="bg-[#2D4A3E] hover:bg-[#1a2e26]"
              data-testid="add-break-confirm-btn"
            >
              Add Break
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/*
        Controlled DropdownMenu rendered ONCE at page level — acts as
        the right-click menu for any event row. Driven by
        ``contextMenuState`` so it pops up at the exact click
        coordinates without per-row Radix Slot wrapping (the wrapping
        previously interfered with dnd-kit's PointerSensor and broke
        drag-to-reorder). The trigger is a tiny invisible anchor moved
        to (x, y) via inline style.
      */}
      <DropdownMenu
        open={contextMenuState.open}
        onOpenChange={(o) => setContextMenuState((s) => ({ ...s, open: o }))}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            style={{
              position: 'fixed',
              left: contextMenuState.x,
              top: contextMenuState.y,
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: 'none',
            }}
          />
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent className="w-64" data-testid="oos-context-menu">
            <DropdownMenuItem
              disabled={!selectionState.adjacentInSameDay || combiningWarmups}
              onSelect={() => { handleCombineWarmups(); closeContextMenu(); }}
              data-testid="ctx-merge-warmups"
            >
              <Link2 className="mr-2 h-4 w-4 text-emerald-600" />
              Merge warm-ups
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!selectionState.anyInCombinedGroup}
              onSelect={() => { handleBulkSplitWarmups(); closeContextMenu(); }}
              data-testid="ctx-split-warmups"
            >
              <Unlink className="mr-2 h-4 w-4 text-stone-600" />
              Split merged warm-up
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                disabled={selectionState.sameDay === null || competitionDays.length <= 1}
                data-testid="ctx-move-to-day"
              >
                <ArrowRightLeft className="mr-2 h-4 w-4 text-blue-600" />
                Move to another day
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {competitionDays
                  .filter((d) => d.dayNumber !== selectionState.sameDay)
                  .map((d) => (
                    <DropdownMenuItem
                      key={d.dayNumber}
                      onSelect={() => { handleBulkMoveToDay(d.dayNumber); closeContextMenu(); }}
                      data-testid={`ctx-move-to-day-${d.dayNumber}`}
                    >
                      Day {d.dayNumber} — {d.dateStr}
                    </DropdownMenuItem>
                  ))}
                {competitionDays.filter((d) => d.dayNumber !== selectionState.sameDay).length === 0 && (
                  <DropdownMenuItem disabled>No other days available</DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                disabled={selectionState.sameDay === null}
                data-testid="ctx-move-to-slot"
              >
                <Clock className="mr-2 h-4 w-4 text-stone-600" />
                Move to time slot
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  onSelect={() => { handleBulkMoveToTimeSlot('morning'); closeContextMenu(); }}
                  data-testid="ctx-slot-morning"
                >
                  <Sun className="mr-2 h-4 w-4 text-amber-500" />
                  Morning
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => { handleBulkMoveToTimeSlot('afternoon'); closeContextMenu(); }}
                  data-testid="ctx-slot-afternoon"
                >
                  <Sunset className="mr-2 h-4 w-4 text-orange-500" />
                  Afternoon
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => { handleBulkMoveToTimeSlot('evening'); closeContextMenu(); }}
                  data-testid="ctx-slot-evening"
                >
                  <Moon className="mr-2 h-4 w-4 text-indigo-500" />
                  Evening
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={selectionState.allShowcase}
              onSelect={() => { handleBulkSetShowcase(true); closeContextMenu(); }}
              data-testid="ctx-set-showcase"
            >
              <Sparkles className="mr-2 h-4 w-4 text-purple-500" />
              Set as Showcase
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!selectionState.anyShowcase}
              onSelect={() => { handleBulkSetShowcase(false); closeContextMenu(); }}
              data-testid="ctx-unset-showcase"
            >
              <Sparkles className="mr-2 h-4 w-4 text-stone-400" />
              Unset Showcase
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={selectionState.allLocked}
              onSelect={() => { handleBulkSetLocked(true); closeContextMenu(); }}
              data-testid="ctx-lock"
            >
              <Lock className="mr-2 h-4 w-4 text-amber-600" />
              Lock event{selectionState.count !== 1 ? 's' : ''} in place
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!selectionState.anyLocked}
              onSelect={() => { handleBulkSetLocked(false); closeContextMenu(); }}
              data-testid="ctx-unlock"
            >
              <LockOpen className="mr-2 h-4 w-4 text-stone-600" />
              Unlock event{selectionState.count !== 1 ? 's' : ''}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!selectionState.isSingle}
              onSelect={() => { handleInsertBreakRelative(selectionState.singleEvent, 'before'); closeContextMenu(); }}
              data-testid="ctx-insert-break-before"
            >
              <PlusCircle className="mr-2 h-4 w-4 text-amber-600" />
              Insert break before
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!selectionState.isSingle}
              onSelect={() => { handleInsertBreakRelative(selectionState.singleEvent, 'after'); closeContextMenu(); }}
              data-testid="ctx-insert-break-after"
            >
              <PlusCircle className="mr-2 h-4 w-4 text-amber-600" />
              Insert break after
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => { handleCopyEventTimes(); closeContextMenu(); }}
              data-testid="ctx-copy-times"
            >
              <Copy className="mr-2 h-4 w-4 text-stone-600" />
              Copy event time{selectionState.count !== 1 ? 's' : ''} as text
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
    </div>
  );
}
